<template>
  <div class="relative">
    <q-inner-loading :showing="isLoading">
      <q-spinner-gears size="100px" color="primary" />
    </q-inner-loading>

    <!-- Error Banner -->
    <q-banner
      v-if="error"
      inline-actions
      rounded
      class="bg-gradient-to-r from-orange-500 to-red-500 text-white m-4 rounded-xl"
    >
      <q-icon name="error" color="white" size="2em" />
      <span class="ml-2 font-semibold">{{ error }}</span>
    </q-banner>


        <!-- Main Content -->
        <div class="p-6">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- 左侧：信号订阅功能 -->
            <div class="space-y-6">
              <!-- 基本车辆信息卡片 -->
              <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
                <div class="flex items-center gap-3 mb-4">
                  <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <q-icon name="directions_car" class="text-white text-xl" />
                  </div>
                  <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">基本车辆信息</h2>
                </div>
                <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700/50">
                  <div class="flex items-center justify-between">
                    <span class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">车辆VIN</span>
                    <span class="text-lg font-mono font-bold text-blue-700 dark:text-blue-300">{{ vinInfo || "加载中..." }}</span>
                  </div>
                </div>
              </div>

              <!-- 信号订阅管理卡片 -->
              <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                    <q-icon name="notifications_active" class="text-white text-xl" />
                  </div>
                  <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">信号订阅管理</h2>
                </div>

                <!-- 批量订阅信号 -->
                <div class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                    <q-icon name="add_circle" class="text-green-500" />
                    批量订阅信号
                  </h3>
                  <div class="space-y-4">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm text-gray-600 dark:text-gray-400">
                        已选择 {{ selectedSignalsForSubscription.length }} / {{ availableSignals.length }}
                      </span>
                      <button
                        @click="toggleSelectAll"
                        class="px-4 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors duration-200 flex items-center gap-1"
                      >
                        <q-icon :name="isAllSelected ? 'check_box' : 'check_box_outline_blank'" size="18px" />
                        <span>{{ isAllSelected ? '取消全选' : '全选' }}</span>
                      </button>
                    </div>
                    <q-select
                      v-model="selectedSignalsForSubscription"
                      :options="availableSignals"
                      label="选择要订阅的信号"
                      multiple
                      outlined
                      dense
                      class="w-full"
                      use-chips
                      stack-label
                      bg-color="white"
                    />
                    <div class="flex gap-3">
                      <button
                        @click="subscribeSelectedSignals"
                        :disabled="selectedSignalsForSubscription.length === 0"
                        class="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                      >
                        <div class="flex items-center justify-center gap-2">
                          <q-icon name="check_circle" />
                          <span>订阅选中信号</span>
                        </div>
                      </button>
                      <button
                        @click="unsubscribeAllSignals"
                        :disabled="Object.keys(subscribedSignals).length === 0"
                        class="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                      >
                        <div class="flex items-center justify-center gap-2">
                          <q-icon name="cancel" />
                          <span>取消所有订阅</span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- 已订阅信号显示 -->
                <div
                  v-if="Object.keys(subscribedSignals).length > 0"
                  class="mt-6"
                >
                  <h4 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                    <q-icon name="list" class="text-blue-500" />
                    已订阅信号
                    <span class="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-sm font-bold">
                      {{ Object.keys(subscribedSignals).length }}
                    </span>
                  </h4>
                  <div class="grid grid-cols-1 gap-3 max-h-[500px] overflow-y-auto pr-2">
                    <div
                      v-for="(data, propertyName) in subscribedSignals"
                      :key="propertyName"
                      class="group relative bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-4 border border-blue-200 dark:border-blue-700/50 hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                    >
                      <div class="flex justify-between items-start mb-3">
                        <span class="font-bold text-gray-800 dark:text-gray-200 text-sm uppercase tracking-wide">
                          {{ getSignalLabel(propertyName) }}
                        </span>
                        <q-btn
                          size="sm"
                          flat
                          round
                          icon="close"
                          @click="unsubscribeSignal(propertyName)"
                          class="text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                        />
                      </div>
                      <div class="text-2xl font-bold text-blue-700 dark:text-blue-300 mb-2 font-mono">
                        {{ data.value || "--" }}
                      </div>
                      <div class="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <q-icon name="schedule" size="14px" />
                        <span>
                          更新时间:
                          {{
                            data.timestamp
                              ? formatTimestamp(data.timestamp)
                              : "--"
                          }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 右侧：信号设置功能 -->
            <div class="space-y-6">
              <!-- 信号设置卡片 -->
              <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <q-icon name="settings" class="text-white text-xl" />
                  </div>
                  <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">信号设置</h2>
                </div>

                <!-- 设置信号值 -->
                <div class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                    <q-icon name="edit" class="text-purple-500" />
                    设置信号值
                  </h3>
                  <div class="space-y-4">
                    <q-select
                      v-model="selectedSignalForSetting"
                      :options="availableSignals"
                      label="选择要设置的信号"
                      outlined
                      dense
                      class="w-full"
                      bg-color="white"
                    />

                    <div v-if="selectedSignalForSetting" class="space-y-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700/50">
                      <q-input
                        v-model="signalValueToSet"
                        :label="`设置 ${selectedSignalForSetting.label} 的值`"
                        outlined
                        dense
                        type="number"
                        bg-color="white"
                      />

                      <q-select
                        v-model="valueType"
                        :options="valueTypeOptions"
                        label="值类型"
                        outlined
                        dense
                        bg-color="white"
                      />

                      <button
                        @click="setSignalValue"
                        :disabled="!selectedSignalForSetting || signalValueToSet === ''"
                        class="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                      >
                        <div class="flex items-center justify-center gap-2">
                          <q-icon name="save" />
                          <span>设置值</span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- 获取信号值 -->
                <div class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                    <q-icon name="search" class="text-cyan-500" />
                    获取信号值
                  </h3>
                  <div class="space-y-4">
                    <q-select
                      v-model="selectedSignalForGetting"
                      :options="availableSignals"
                      label="选择要获取的信号"
                      outlined
                      dense
                      class="w-full"
                      bg-color="white"
                    />

                    <div class="flex gap-3">
                      <button
                        @click="getSingleSignalValue"
                        :disabled="!selectedSignalForGetting"
                        class="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                      >
                        <div class="flex items-center justify-center gap-2">
                          <q-spinner-dots v-if="isGettingSignal" class="text-white" />
                          <q-icon v-else name="download" />
                          <span>获取值</span>
                        </div>
                      </button>
                      <button
                        @click="clearSignalValue"
                        :disabled="!selectedSignalForGetting || !currentSignalValue"
                        class="flex-1 px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                      >
                        <div class="flex items-center justify-center gap-2">
                          <q-icon name="clear" />
                          <span>清空</span>
                        </div>
                      </button>
                    </div>

                    <!-- 显示获取到的信号值 -->
                    <div
                      v-if="currentSignalValue && selectedSignalForGetting"
                      class="mt-4 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30 rounded-xl p-5 border border-cyan-200 dark:border-cyan-700/50"
                    >
                      <div class="font-bold text-lg text-gray-800 dark:text-gray-200 mb-3">
                        {{ selectedSignalForGetting.label }}
                      </div>
                      <div class="text-3xl font-mono font-bold text-cyan-700 dark:text-cyan-300 mb-4">
                        {{ currentSignalValue.value || "--" }}
                      </div>
                      <div class="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                        <div>属性ID: <span class="font-mono font-semibold">{{ currentSignalValue.propertyId }}</span></div>
                        <div>
                          状态: 
                          <span class="font-semibold">
                            {{ translateStatus(currentSignalValue.status) }}
                          </span>
                        </div>
                        <div>区域ID: <span class="font-mono font-semibold">{{ currentSignalValue.areaId }}</span></div>
                        <div>
                          时间戳:
                          <span class="font-mono font-semibold">
                            {{
                              currentSignalValue.timestamp
                                ? formatTimestamp(currentSignalValue.timestamp)
                                : "--"
                            }}
                          </span>
                        </div>
                        <div v-if="currentSignalValue.onChangeFlag !== undefined">
                          变化标志: 
                          <span class="font-semibold">
                            {{ translateOnChangeFlag(currentSignalValue.onChangeFlag) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 信号配置信息 -->
              <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                    <q-icon name="info" class="text-white text-xl" />
                  </div>
                  <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">信号配置信息</h2>
                </div>

                <div class="space-y-4">
                  <q-select
                    v-model="selectedSignalForConfig"
                    :options="availableSignals"
                    label="选择要查看的信号"
                    outlined
                    dense
                    class="w-full"
                    bg-color="white"
                  />

                  <div class="flex gap-3">
                    <button
                      @click="getSignalConfig"
                      :disabled="!selectedSignalForConfig"
                      class="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                    >
                      <div class="flex items-center justify-center gap-2">
                        <q-spinner-dots v-if="isLoadingConfig" class="text-white" />
                        <q-icon v-else name="search" />
                        <span>获取配置信息</span>
                      </div>
                    </button>
                    <button
                      @click="clearSignalConfig"
                      :disabled="!currentSignalConfig"
                      class="flex-1 px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                    >
                      <div class="flex items-center justify-center gap-2">
                        <q-icon name="clear" />
                        <span>清空</span>
                      </div>
                    </button>
                  </div>

                  <div
                    v-if="currentSignalConfig"
                    class="mt-4 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 rounded-xl p-5 border border-amber-200 dark:border-amber-700/50"
                  >
                    <div class="flex items-center justify-between mb-3">
                      <span class="font-bold text-gray-800 dark:text-gray-200">
                        {{ selectedSignalForConfig?.label || currentSignalConfig.propertyName }}
                      </span>
                      <span class="text-sm font-mono text-gray-600 dark:text-gray-300">
                        ID: {{ currentSignalConfig.propertyId }}
                      </span>
                    </div>
                    <div class="grid grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300">
                      <div>
                        访问模式: 
                        <span class="font-semibold">
                          {{ translateAccessMode(currentSignalConfig.access) }}
                        </span>
                      </div>
                      <div>是否可读: <span class="font-semibold">{{ currentSignalConfig.isReadable ? "是" : "否" }}</span></div>
                      <div>是否可写: <span class="font-semibold">{{ currentSignalConfig.isWritable ? "是" : "否" }}</span></div>
                      <div v-if="currentSignalConfig.changeMode !== undefined">
                        变化模式: 
                        <span class="font-semibold">
                          {{ translateChangeMode(currentSignalConfig.changeMode) }}
                        </span>
                      </div>
                      <div v-if="currentSignalConfig.type">
                        属性类型: <span class="font-mono font-semibold">{{ currentSignalConfig.type }}</span>
                      </div>
                      <div>access: <span class="font-mono">{{ currentSignalConfig.access }}</span></div>
                      <div>minSampleRate: <span class="font-mono">{{ currentSignalConfig.minSampleRate }}</span></div>
                      <div>maxSampleRate: <span class="font-mono">{{ currentSignalConfig.maxSampleRate }}</span></div>
                      <div
                        v-if="currentSignalConfig.supportedAreas && currentSignalConfig.supportedAreas.length > 0"
                        class="col-span-2 mt-2 pt-2 border-t border-amber-200 dark:border-amber-700/50"
                      >
                        <div class="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                          支持的区域 ({{ currentSignalConfig.supportedAreas.length }}):
                        </div>
                        <div class="space-y-1 max-h-32 overflow-y-auto">
                          <div
                            v-for="area in currentSignalConfig.supportedAreas"
                            :key="area.areaId"
                            class="text-xs bg-white/50 dark:bg-gray-800/50 rounded p-1"
                          >
                            <div class="font-mono">
                              AreaId: {{ area.areaId }}
                            </div>
                            <div v-if="area.minValue !== null && area.minValue !== undefined" class="text-gray-600 dark:text-gray-400">
                              最小值: {{ area.minValue }}
                            </div>
                            <div v-if="area.maxValue !== null && area.maxValue !== undefined" class="text-gray-600 dark:text-gray-400">
                              最大值: {{ area.maxValue }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 车辆诊断信息卡片 -->
              <div class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600">
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                    <q-icon name="medical_services" class="text-white text-xl" />
                  </div>
                  <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">车辆诊断信息</h2>
                </div>

                <div class="space-y-4">
                  <div class="flex items-center gap-3">
                    <q-select
                      color="teal"
                      v-model="selectedVehicleInfo"
                      :options="availableVehicleInfos"
                      label="选择诊断信息"
                      outlined
                      dense
                      class="flex-1"
                      @update:model-value="getVehicleInfoValue"
                      bg-color="white"
                    />
                    <button
                      @click="getVehicleInfoValue"
                      class="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                      <div class="flex items-center gap-2">
                        <q-icon name="refresh" />
                        <span>获取值</span>
                      </div>
                    </button>
                  </div>

                  <div
                    v-if="selectedVehicleInfoValue !== null"
                    class="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30 rounded-xl p-5 border border-teal-200 dark:border-teal-700/50"
                  >
                    <div class="flex items-center justify-between">
                      <span class="font-bold text-gray-800 dark:text-gray-200">
                        {{ selectedVehicleInfo?.label || "未选择" }}
                      </span>
                      <span class="text-2xl font-mono font-bold text-teal-700 dark:text-teal-300">
                        {{ selectedVehicleInfoValue }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useCar } from "@/composables/useCar";
import { useQuasar } from "quasar";
import {
  CarClient,
  formatTimestamp,
  translateAccessMode,
  translateChangeMode,
  translateStatus,
  translateOnChangeFlag,
} from "@/utils/capacitor/carPlugin";

const $q = useQuasar();

const {
  isLoading,
  error,
  getProperty,
  setProperty,
  subscribeToProperty,
  VehiclePropertyNames,
  getVehicleInfo,
  VehicleInfoIds,
  unsubscribeToProperty,
} = useCar();

// 状态变量
const vinInfo = ref("");

// 信号选择器相关
interface SignalOption {
  value: keyof typeof VehiclePropertyNames;
  label: string;
}

// 可用信号列表
const availableSignals = ref<SignalOption[]>([
  { value: "VehSpd", label: "车速" },
  { value: "ActuGearShiftPos", label: "档位" },
  // { value: "HuFrtACSwReq", label: "空调开关" },
  // 经纬度 - INS相关
  { value: "INS_MSG_InsLatitude", label: "INS纬度(MSG)" },
  { value: "INS_MSG_InsLongitude", label: "INS经度(MSG)" },
  { value: "INS_Latitude", label: "INS纬度" },
  { value: "INS_Longitude", label: "INS经度" },
  { value: "INS_Latitude_AD4", label: "INS纬度(AD4)" },
  { value: "INS_Longitude_AD4", label: "INS经度(AD4)" },
  // 经纬度 - GNSS相关
  { value: "GNSSLOC_Latitude", label: "GNSS纬度" },
  { value: "GNSSLOC_Longitude", label: "GNSS经度" },
  { value: "GNSSLOC_Latitude_AD4", label: "GNSS纬度(AD4)" },
  { value: "GNSSLOC_Longitude_AD4", label: "GNSS经度(AD4)" },
  { value: "GNSSLOC_MSG_GnssLocLatitude", label: "GNSS纬度(MSG)" },
  { value: "GNSSLOC_MSG_GnssLocLongitude", label: "GNSS经度(MSG)" },
  // { value: "EPS_StrgAngSpeed", label: "方向盘角速度" },
  { value: "EPS_StrgAng", label: "方向盘角度" },
  { value: "MATRIX_SOA_VERSION", label: "MATRIX_SOA_VERSION" },
  // { value: "MATRIX_ECAN_VERSION", label: "MATRIX_ECAN_VERSION" },
  // { value: "MATRIX_ICAN_VERSION", label: "MATRIX_ICAN_VERSION" },
  // { value: "MATRIX_MCU_VERSION", label: "MATRIX_MCU_VERSION" },
  // 前方道路特征
  { value: "FrontRoadFeature", label: "前方道路特征" },
  { value: "FrontRoadFeatureDistance", label: "前方道路特征距离" },
  { value: "FrontRoadFeatureLevel", label: "前方道路特征等级" },
  { value: "FrontRoadFeatureLength", label: "前方道路特征长度" },
  { value: "FrontRoadFeatureLevelMax", label: "前方道路特征等级最大值" },
  { value: "FrontRoadFeatureLevelMin", label: "前方道路特征等级最小值" },
  { value: "FrontRoadFeatureSts", label: "前方道路特征状态" },
  // 悬架相关
  { value: "SusComfortLevelDisplay", label: "悬架舒适度等级显示" },
  { value: "SusDamperLevelDisplay", label: "悬架阻尼等级显示" },
  { value: "SusIMUAccVert", label: "悬架IMU垂直加速度" },
  { value: "SusWhlAccFL", label: "悬架左前轮加速度" },
  { value: "SusWhlAccFR", label: "悬架右前轮加速度" },
  { value: "SusActuHghtLvl", label: "悬架执行器高度等级" },
  { value: "SusActuChmbMd", label: "悬架执行器腔室模式" },
  // INS航向角
  { value: "INS_HeadingAngle", label: "INS航向角" },
  { value: "INS_HeadingAngle_AD4", label: "INS航向角(AD4)" },
  { value: "INS_MSG_InsHeadingAngle", label: "INS航向角(MSG)" },
]);

// 多选订阅相关
const selectedSignalsForSubscription = ref<SignalOption[]>([]);
const subscribedSignals = ref<Record<string, any>>({});
const unsubscribeFunctions = ref<Record<string, () => Promise<void>>>({});

// 单选设置相关
const selectedSignalForSetting = ref<SignalOption | null>(null);
const signalValueToSet = ref<string>("");
const valueType = ref<string>("int");

// 单选获取相关
const selectedSignalForGetting = ref<SignalOption | null>(null);
const currentSignalValue = ref<any>(null);
const isGettingSignal = ref(false);

// 配置信息查询相关
const selectedSignalForConfig = ref<SignalOption | null>(null);
const currentSignalConfig = ref<any | null>(null);
const isLoadingConfig = ref(false);

const valueTypeOptions = [
  { label: "整数", value: "int" },
  { label: "浮点数", value: "float" },
  { label: "布尔值", value: "boolean" },
];

// 车辆诊断信息选择器相关
interface VehicleInfoOption {
  value: string;
  label: string;
}

const selectedVehicleInfo = ref<VehicleInfoOption | null>(null);
const selectedVehicleInfoValue = ref<string | null>(null);

// 生成可用的诊断信息选项
const availableVehicleInfos = computed<VehicleInfoOption[]>(() => {
  return Object.entries(VehicleInfoIds).map(([key, value]) => ({
    value: value,
    label: key,
  }));
});

// 获取信号标签
const getSignalLabel = (propertyName: string): string => {
  const signal = availableSignals.value.find((s) => s.value === propertyName);
  return signal?.label || propertyName;
};

// 判断是否已全选
const isAllSelected = computed(() => {
  return selectedSignalsForSubscription.value.length === availableSignals.value.length && availableSignals.value.length > 0;
});

// 全选/取消全选
const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedSignalsForSubscription.value = [];
  } else {
    selectedSignalsForSubscription.value = [...availableSignals.value];
  }
};

// 订阅选中的信号
const subscribeSelectedSignals = async () => {
  if (selectedSignalsForSubscription.value.length === 0) return;

  try {
    for (const signal of selectedSignalsForSubscription.value) {
      // 如果已经订阅了，先取消订阅
      if (unsubscribeFunctions.value[signal.value]) {
        await unsubscribeFunctions.value[signal.value]();
      }

      // 订阅新信号
      const unsubscribe = await subscribeToProperty(signal.value, (data) => {
        console.log("[BOWEN_LOG] 🚀 ~~ unsubscribe ~~ data:", data);

        subscribedSignals.value[signal.value] = {
          value: data.value,
          timestamp: data.timestamp,
          status: data.status,
          areaId: data.areaId,
        };
      });

      unsubscribeFunctions.value[signal.value] = unsubscribe;

      // 初始化显示数据
      if (!subscribedSignals.value[signal.value]) {
        subscribedSignals.value[signal.value] = {
          value: "--",
          timestamp: null,
          status: null,
          areaId: null,
        };
      }
    }

    $q.notify({
      type: "positive",
      message: `成功订阅 ${selectedSignalsForSubscription.value.length} 个信号`,
    });

    // 清空选择
    selectedSignalsForSubscription.value = [];
  } catch (err) {
    console.error("订阅信号失败:", err);
    $q.notify({
      type: "negative",
      message: "订阅信号失败",
    });
  }
};

// 取消单个信号订阅
const unsubscribeSignal = async (propertyName: string) => {
  try {
    if (unsubscribeFunctions.value[propertyName]) {
      await unsubscribeFunctions.value[propertyName]();
      delete unsubscribeFunctions.value[propertyName];
    }
    delete subscribedSignals.value[propertyName];

    $q.notify({
      type: "positive",
      message: `已取消订阅 ${getSignalLabel(propertyName)}`,
    });
  } catch (err) {
    console.error("取消订阅失败:", err);
    $q.notify({
      type: "negative",
      message: "取消订阅失败",
    });
  }
};

// 取消所有信号订阅
const unsubscribeAllSignals = async () => {
  try {
    const propertyNames = Object.keys(subscribedSignals.value);

    for (const propertyName of propertyNames) {
      if (unsubscribeFunctions.value[propertyName]) {
        await unsubscribeFunctions.value[propertyName]();
      }
    }

    // 清空所有数据
    subscribedSignals.value = {};
    unsubscribeFunctions.value = {};

    $q.notify({
      type: "positive",
      message: `已取消所有 ${propertyNames.length} 个信号的订阅`,
    });
  } catch (err) {
    console.error("取消所有订阅失败:", err);
    $q.notify({
      type: "negative",
      message: "取消所有订阅失败",
    });
  }
};

// 设置信号值
const setSignalValue = async () => {
  if (!selectedSignalForSetting.value || signalValueToSet.value === "") return;

  try {
    // 先检查属性是否可写
    try {
      const config = await CarClient.getPropertyConfig({
        propertyName: selectedSignalForSetting.value.value,
      });
      console.log("[BOWEN_LOG] 🚀 ~~ setSignalValue ~~ config:", config);
      
      if (!config.isWritable) {
        $q.notify({
          type: "warning",
          message: `${selectedSignalForSetting.value.label} 是只读属性，无法设置。该属性只能读取或订阅。`,
          timeout: 5000,
        });
        return;
      }
    } catch (configErr) {
      // 如果获取配置失败，继续尝试设置（向后兼容）
      console.warn("无法获取属性配置，继续尝试设置:", configErr);
    }

    let value: number | boolean | string = signalValueToSet.value;

    // 根据类型转换值
    if (valueType.value === "int") {
      value = parseInt(signalValueToSet.value);
    } else if (valueType.value === "float") {
      value = parseFloat(signalValueToSet.value);
    } else if (valueType.value === "boolean") {
      value =
        signalValueToSet.value === "1" ||
        signalValueToSet.value.toLowerCase() === "true";
    }

    await setProperty(
      selectedSignalForSetting.value.value,
      value,
      valueType.value as "int" | "float" | "boolean"
    );

    $q.notify({
      type: "positive",
      message: `成功设置 ${selectedSignalForSetting.value.label} = ${value}`,
    });

    // 清空输入
    signalValueToSet.value = "";
  } catch (err: any) {
    console.error("设置信号值失败:", err);
    
    // 检查是否是只读属性错误
    const errorMessage = err?.message || String(err);
    let notifyMessage = "设置信号值失败";
    
    if (errorMessage.includes("read-only") || errorMessage.includes("只读") || errorMessage.includes("READ_ONLY")) {
      notifyMessage = `${selectedSignalForSetting.value?.label || "该属性"} 是只读属性，无法设置。该属性只能读取或订阅。`;
    } else if (errorMessage.includes("Failed to set value")) {
      notifyMessage = `${selectedSignalForSetting.value?.label || "该属性"} 设置失败，可能是只读属性或值超出范围。`;
    }
    
    $q.notify({
      type: "negative",
      message: notifyMessage,
      timeout: 5000,
    });
  }
};

// 获取单个信号值
const getSingleSignalValue = async () => {
  if (!selectedSignalForGetting.value) return;

  isGettingSignal.value = true;
  try {
    const result = await getProperty(selectedSignalForGetting.value.value);
    console.log("[BOWEN_LOG] 🚀 ~~ getSingleSignalValue ~~ result:", result);
    currentSignalValue.value = result;

    $q.notify({
      type: "positive",
      message: `成功获取 ${selectedSignalForGetting.value.label} 的值`,
    });
  } catch (err) {
    console.error("获取信号值失败:", err);
    $q.notify({
      type: "negative",
      message: "获取信号值失败",
    });
    currentSignalValue.value = null;
  } finally {
    isGettingSignal.value = false;
  }
};

// 清空信号值
const clearSignalValue = () => {
  currentSignalValue.value = null;
  $q.notify({
    type: "info",
    message: "已清空信号值",
  });
};

// 获取信号配置信息
const getSignalConfig = async () => {
  if (!selectedSignalForConfig.value) return;
  isLoadingConfig.value = true;
  try {
    const config = await CarClient.getPropertyConfig({
      propertyName: selectedSignalForConfig.value.value,
    });
    currentSignalConfig.value = config;
    $q.notify({
      type: "positive",
      message: `已获取 ${selectedSignalForConfig.value.label} 的配置信息`,
    });
  } catch (err) {
    console.error("获取信号配置信息失败:", err);
    $q.notify({
      type: "negative",
      message: "获取信号配置信息失败",
    });
    currentSignalConfig.value = null;
  } finally {
    isLoadingConfig.value = false;
  }
};

// 清空信号配置信息
const clearSignalConfig = () => {
  currentSignalConfig.value = null;
  $q.notify({
    type: "info",
    message: "已清空信号配置信息",
  });
};

// 获取选中的诊断信息值
const getVehicleInfoValue = async () => {
  if (!selectedVehicleInfo.value) return;

  try {
    const infoId = selectedVehicleInfo.value.value;
    const result = await getVehicleInfo(infoId);
    selectedVehicleInfoValue.value = result.value || "未获取到值";
  } catch (err) {
    console.error(`获取诊断信息值失败:`, err);
    selectedVehicleInfoValue.value = "获取失败";
  }
};

// 获取车辆信息
const getCarInfo = async () => {
  try {
    // 获取VIN信息
    const vinResult = await getVehicleInfo(VehicleInfoIds.VEHICLE_VIN_ID);
    console.log("[BOWEN_LOG] 🚀 ~~ getCarInfo ~~ vinResult:", vinResult);
    vinInfo.value = vinResult.value;
  } catch (error) {
    console.error("获取车辆信息失败", error);
  }
};

// 组件挂载时
onMounted(async () => {
  // 初始化数据
  await getCarInfo();
});

// 组件卸载时
onUnmounted(async () => {
  try {
    // 取消当前显示的所有订阅
    await unsubscribeAllSignals();

    // 额外确保所有可用信号都被取消订阅
    console.log("开始确保所有可用信号都被取消订阅...");
    for (const signal of availableSignals.value) {
      try {
        // 即使信号不在当前订阅列表中，也尝试取消订阅它
        await unsubscribeToProperty(signal.value);
      } catch (error) {
        // 忽略错误，继续处理下一个信号
        console.warn(`尝试取消订阅 ${signal.label} 时出现非致命错误:`, error);
      }
    }
    console.log("所有可用信号取消订阅检查完成");
  } catch (error) {
    console.error("组件卸载时清理订阅失败:", error);
  }
});
</script>

<style scoped>
/* 自定义滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
